import { useState, useEffect, useRef } from "react";
import { generateAdmissionPattern } from "@/services/aiService";
import { useNavigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Play,
  ChevronLeft,
  ChevronRight,
  Save,
  Settings2,
  Hash,
  Calendar,
  Type,
  FileText,
  Shield,
  List,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Copy,
  MoreVertical,
  Sparkles,
  Upload,
  HelpCircle,
  Send,
  X,
  Bot,
  User
} from "lucide-react";
import Header from "./Header";
import AdminSidebar from "./Sidebar";
import { db, auth } from "@/firebase/config";
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, where, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { toast, Toaster } from "sonner";

// ============================================================================
// TYPES
// ============================================================================

type ComponentType = 'static' | 'year' | 'semester' | 'sequence' | 'alpha-sequence' | 'checksum' | 'lookup' | 'date-part';
type YearFormat = 'YYYY' | 'YY';
type ChecksumAlgorithm = 'luhn' | 'mod10' | 'mod11' | 'verhoeff';
type ResetOn = 'year' | 'semester' | 'never';
type OverflowBehavior = 'error' | 'carry' | 'wrap';
type YearResetRule = 'calendar' | 'academic' | 'fiscal' | 'none';

interface ComponentConfig {
  id: string;
  key: string;
  type: ComponentType;
  value?: string;           // For static
  format?: YearFormat;      // For year
  length?: number;          // For sequence, alpha-sequence
  min?: number;             // For sequence
  max?: number;             // For sequence
  alphabet?: string;        // For alpha-sequence
  checksumAlg?: ChecksumAlgorithm;
  resetOn?: ResetOn;
  carryTo?: string;         // Key of component to carry to
  lookupValues?: string[];  // For lookup (department codes)
}

interface PatternDefinition {
  id?: string;
  name: string;
  template: string;
  components: ComponentConfig[];
  yearResetRule: YearResetRule;
  academicYearStart: { month: number; day: number };
  onOverflow: OverflowBehavior;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ParsedComponent {
  key: string;
  value: string | number;
  type: ComponentType;
}

interface ParseResult {
  success: boolean;
  components: ParsedComponent[];
  error?: string;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const generateId = () => `comp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const getComponentIcon = (type: ComponentType) => {
  switch (type) {
    case 'static': return <Type className="h-4 w-4" />;
    case 'year': return <Calendar className="h-4 w-4" />;
    case 'semester': return <Calendar className="h-4 w-4" />;
    case 'sequence': return <Hash className="h-4 w-4" />;
    case 'alpha-sequence': return <Type className="h-4 w-4" />;
    case 'checksum': return <Shield className="h-4 w-4" />;
    case 'lookup': return <List className="h-4 w-4" />;
    case 'date-part': return <Calendar className="h-4 w-4" />;
    default: return <FileText className="h-4 w-4" />;
  }
};

const getComponentLabel = (type: ComponentType) => {
  switch (type) {
    case 'static': return 'Static Text';
    case 'year': return 'Year';
    case 'semester': return 'Semester';
    case 'sequence': return 'Sequence';
    case 'alpha-sequence': return 'Alpha Sequence';
    case 'checksum': return 'Checksum';
    case 'lookup': return 'Lookup';
    case 'date-part': return 'Date Part';
    default: return type;
  }
};

const getComponentColor = (type: ComponentType) => {
  switch (type) {
    case 'static': return 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600';
    case 'year': return 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600';
    case 'semester': return 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-600';
    case 'sequence': return 'bg-green-500/20 text-green-700 dark:text-green-300 border-green-300 dark:border-green-600';
    case 'alpha-sequence': return 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-600';
    case 'checksum': return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600';
    case 'lookup': return 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-600';
    case 'date-part': return 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-600';
    default: return 'bg-gray-500/20 text-gray-700 dark:text-gray-300';
  }
};

// Generate template string from components
const generateTemplateString = (components: ComponentConfig[]): string => {
  return components.map(comp => {
    switch (comp.type) {
      case 'static':
        return comp.value || '';
      case 'year':
        return `{YEAR:${comp.format || 'YYYY'}}`;
      case 'semester':
        return '{SEM}';
      case 'sequence':
        const seqParts = [comp.length || 4];
        if (comp.min !== undefined) seqParts.push(comp.min);
        if (comp.max !== undefined) seqParts.push(comp.max);
        return `{SEQ:${seqParts.join(':')}}`;
      case 'alpha-sequence':
        return `{ALPHA:${comp.length || 2}${comp.alphabet ? ':' + comp.alphabet : ''}}`;
      case 'checksum':
        return `{CHK:${comp.checksumAlg || 'luhn'}}`;
      case 'lookup':
        return `{${comp.key.toUpperCase()}}`;
      case 'date-part':
        return `{DATE:${comp.format || 'MM'}}`;
      default:
        return '';
    }
  }).join('');
};

// Generate preview ID from components
const generatePreviewId = (components: ComponentConfig[]): string => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  return components.map(comp => {
    switch (comp.type) {
      case 'static':
        return comp.value || '';
      case 'year':
        return comp.format === 'YY' ? String(currentYear).slice(-2) : String(currentYear);
      case 'semester':
        return currentMonth < 7 ? '1' : '2';
      case 'sequence':
        return String(comp.min || 1).padStart(comp.length || 4, '0');
      case 'alpha-sequence':
        const len = comp.length || 2;
        return 'A'.repeat(len) + '001'.slice(-3);
      case 'checksum':
        return '0'; // Placeholder
      case 'lookup':
        return comp.lookupValues?.[0] || 'DEPT';
      case 'date-part':
        return String(currentMonth).padStart(2, '0');
      default:
        return '';
    }
  }).join('');
};

// ============================================================================
// CHECKSUM ALGORITHMS
// ============================================================================

const calculateLuhn = (digits: string): number => {
  let sum = 0;
  let isEven = true;
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    isEven = !isEven;
  }
  return (10 - (sum % 10)) % 10;
};

const calculateMod10 = (digits: string): number => {
  const sum = digits.split('').reduce((acc, d) => acc + parseInt(d, 10), 0);
  return (10 - (sum % 10)) % 10;
};

const calculateMod11 = (digits: string): string => {
  const weights = [2, 3, 4, 5, 6, 7, 2, 3, 4, 5, 6, 7];
  let sum = 0;
  const reversed = digits.split('').reverse();
  for (let i = 0; i < reversed.length; i++) {
    sum += parseInt(reversed[i], 10) * weights[i % weights.length];
  }
  const remainder = sum % 11;
  const check = 11 - remainder;
  if (check === 10) return 'X';
  if (check === 11) return '0';
  return String(check);
};

// ============================================================================
// PARSER
// ============================================================================

const parseAdmissionNumber = (input: string, components: ComponentConfig[]): ParseResult => {
  const result: ParsedComponent[] = [];
  let cursor = 0;
  
  for (const comp of components) {
    if (cursor >= input.length) {
      return { success: false, components: [], error: `Unexpected end of input at component ${comp.key}` };
    }
    
    switch (comp.type) {
      case 'static': {
        const expected = comp.value || '';
        const actual = input.slice(cursor, cursor + expected.length);
        if (actual !== expected) {
          return { success: false, components: [], error: `Expected "${expected}" at position ${cursor}, got "${actual}"` };
        }
        result.push({ key: comp.key, value: actual, type: comp.type });
        cursor += expected.length;
        break;
      }
      case 'year': {
        const len = comp.format === 'YY' ? 2 : 4;
        const yearStr = input.slice(cursor, cursor + len);
        const year = parseInt(yearStr, 10);
        if (isNaN(year)) {
          return { success: false, components: [], error: `Invalid year at position ${cursor}` };
        }
        result.push({ key: comp.key, value: year, type: comp.type });
        cursor += len;
        break;
      }
      case 'semester': {
        const sem = input[cursor];
        if (!['1', '2', '3'].includes(sem)) {
          return { success: false, components: [], error: `Invalid semester at position ${cursor}` };
        }
        result.push({ key: comp.key, value: parseInt(sem, 10), type: comp.type });
        cursor += 1;
        break;
      }
      case 'sequence': {
        const len = comp.length || 4;
        const seqStr = input.slice(cursor, cursor + len);
        const seq = parseInt(seqStr, 10);
        if (isNaN(seq)) {
          return { success: false, components: [], error: `Invalid sequence at position ${cursor}` };
        }
        if (comp.min !== undefined && seq < comp.min) {
          return { success: false, components: [], error: `Sequence ${seq} below minimum ${comp.min}` };
        }
        if (comp.max !== undefined && seq > comp.max) {
          return { success: false, components: [], error: `Sequence ${seq} above maximum ${comp.max}` };
        }
        result.push({ key: comp.key, value: seq, type: comp.type });
        cursor += len;
        break;
      }
      case 'alpha-sequence': {
        const len = comp.length || 2;
        const alphaStr = input.slice(cursor, cursor + len);
        const validChars = comp.alphabet || 'A-Z';
        // Simple validation - just check length for now
        result.push({ key: comp.key, value: alphaStr, type: comp.type });
        cursor += len;
        break;
      }
      case 'checksum': {
        const checkDigit = input[cursor];
        result.push({ key: comp.key, value: checkDigit, type: comp.type });
        cursor += 1;
        break;
      }
      case 'lookup': {
        const lookupValues = comp.lookupValues || [];
        let matched = false;
        for (const val of lookupValues.sort((a, b) => b.length - a.length)) {
          if (input.slice(cursor).startsWith(val)) {
            result.push({ key: comp.key, value: val, type: comp.type });
            cursor += val.length;
            matched = true;
            break;
          }
        }
        if (!matched) {
          return { success: false, components: [], error: `Unknown lookup value at position ${cursor}` };
        }
        break;
      }
      case 'date-part': {
        const len = 2; // MM or DD
        const dateStr = input.slice(cursor, cursor + len);
        const dateVal = parseInt(dateStr, 10);
        if (isNaN(dateVal)) {
          return { success: false, components: [], error: `Invalid date part at position ${cursor}` };
        }
        result.push({ key: comp.key, value: dateVal, type: comp.type });
        cursor += len;
        break;
      }
    }
  }
  
  if (cursor !== input.length) {
    return { success: false, components: [], error: `Extra characters after position ${cursor}` };
  }
  
  return { success: true, components: result };
};

// ============================================================================
// GENERATOR
// ============================================================================

const generateNextId = (parsed: ParsedComponent[], components: ComponentConfig[], offset: number = 1): string => {
  const working = parsed.map(p => ({ ...p }));
  
  // Find sequence components (rightmost first for carry logic)
  const seqIndices = components
    .map((c, i) => ({ comp: c, idx: i }))
    .filter(x => x.comp.type === 'sequence' || x.comp.type === 'alpha-sequence')
    .reverse();
  
  if (seqIndices.length === 0) {
    // No incrementable component
    return formatId(working, components);
  }
  
  // Apply offset to rightmost sequence
  const targetIdx = seqIndices[0].idx;
  const targetComp = components[targetIdx];
  const targetParsed = working[targetIdx];
  
  if (targetComp.type === 'sequence') {
    let newVal = (targetParsed.value as number) + offset;
    const min = targetComp.min ?? 1;
    const max = targetComp.max ?? Math.pow(10, targetComp.length || 4) - 1;
    
    if (newVal > max) {
      // Overflow - handle carry or wrap
      newVal = min;
      // TODO: Implement carry logic
    } else if (newVal < min) {
      // Underflow
      newVal = min;
    }
    
    working[targetIdx].value = newVal;
  }
  
  return formatId(working, components);
};

const formatId = (parsed: ParsedComponent[], components: ComponentConfig[]): string => {
  return parsed.map((p, i) => {
    const comp = components[i];
    switch (p.type) {
      case 'static':
        return p.value;
      case 'year':
        return String(p.value);
      case 'semester':
        return String(p.value);
      case 'sequence':
        return String(p.value).padStart(comp.length || 4, '0');
      case 'alpha-sequence':
        return p.value;
      case 'checksum':
        return p.value;
      case 'lookup':
        return p.value;
      case 'date-part':
        return String(p.value).padStart(2, '0');
      default:
        return '';
    }
  }).join('');
};

// ============================================================================
// COMPONENT
// ============================================================================

const AdmissionNumberSetup = () => {
  const navigate = useNavigate();
  
  // State
  const [schoolID, setSchoolID] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patterns, setPatterns] = useState<PatternDefinition[]>([]);
  const [activeTab, setActiveTab] = useState("builder");
  
  // Ref for auto-scrolling chat
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  // Current pattern being edited - ALWAYS includes a sequence component for auto-increment
  const [currentPattern, setCurrentPattern] = useState<PatternDefinition>({
    name: "Default Pattern",
    template: "",
    components: [
      { id: crypto.randomUUID(), type: 'static', value: 'ADM', key: 'prefix' },
      { id: crypto.randomUUID(), type: 'static', value: '/', key: 'sep1' },
      { id: crypto.randomUUID(), type: 'year', format: 'YYYY', key: 'year' },
      { id: crypto.randomUUID(), type: 'static', value: '/', key: 'sep2' },
      { id: crypto.randomUUID(), type: 'sequence', length: 3, resetOn: 'year', min: 1, key: 'seq' }
    ],
    yearResetRule: 'calendar',
    academicYearStart: { month: 9, day: 1 },
    onOverflow: 'error',
    isActive: true
  });
  
  // Dialog state
  const [showAddComponent, setShowAddComponent] = useState(false);
  const [editingComponent, setEditingComponent] = useState<ComponentConfig | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importSamples, setImportSamples] = useState("");
  const [showHelpPage, setShowHelpPage] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // AI Pattern Creator state (following ChatbotPage.tsx pattern)
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiChatInput, setAiChatInput] = useState("");
  const [aiChatHistory, setAiChatHistory] = useState<{role: 'user'|'model', text: string}[]>([]);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiGeneratedPattern, setAiGeneratedPattern] = useState<ComponentConfig[] | null>(null);
  const [isEditingWithAI, setIsEditingWithAI] = useState(false); // Context-aware AI editing
  
  // Test state
  const [testInput, setTestInput] = useState("");
  const [testResult, setTestResult] = useState<ParseResult | null>(null);
  const [generatedNext, setGeneratedNext] = useState("");
  const [generatedPrev, setGeneratedPrev] = useState("");
  
  // Sequence start dialog state
  const [showSequenceDialog, setShowSequenceDialog] = useState(false);
  const [sequenceStartOption, setSequenceStartOption] = useState<'zero' | 'continue'>('zero');
  const [sequenceStartNumber, setSequenceStartNumber] = useState(1);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [patternToDelete, setPatternToDelete] = useState<PatternDefinition | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Auth
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await getIdTokenResult(user);
          const claims = token.claims;
          const aff = (claims.affiliations as any[]) || [];
          const adminAff = aff.find(a => a.role === 'admin' && a.schoolID) || claims.defaultAffiliation;
          if (adminAff?.schoolID) {
            setSchoolID(adminAff.schoolID);
          }
        } catch (e) {
          console.error(e);
        }
      }
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);
  
  // Load patterns
  useEffect(() => {
    if (!schoolID) return;
    
    const q = query(collection(db, "schools", schoolID, "admissionPatterns"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const patternsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PatternDefinition[];
      setPatterns(patternsData);
      
      // Load active pattern or first pattern
      const active = patternsData.find(p => p.isActive) || patternsData[0];
      if (active) {
        setCurrentPattern(active);
      }
    });
    return () => unsubscribe();
  }, [schoolID]);
  
  // Add component
  const addComponent = (type: ComponentType) => {
    const newComp: ComponentConfig = {
      id: generateId(),
      key: `${type}_${Date.now()}`,
      type,
      ...(type === 'static' && { value: '-' }),
      ...(type === 'year' && { format: 'YYYY' }),
      ...(type === 'sequence' && { length: 4, min: 1, max: 9999, resetOn: 'year' }),
      ...(type === 'alpha-sequence' && { length: 2, alphabet: 'A-Z' }),
      ...(type === 'checksum' && { checksumAlg: 'luhn' }),
      ...(type === 'lookup' && { lookupValues: ['DEPT'] }),
    };
    
    setCurrentPattern(prev => ({
      ...prev,
      components: [...prev.components, newComp],
      template: generateTemplateString([...prev.components, newComp])
    }));
    setShowAddComponent(false);
  };
  
  // AI Pattern Creator - Submit Handler (following ChatbotPage.tsx pattern)
  const handleAISubmit = async () => {
    if (!aiChatInput.trim()) return;
    const userMsg = aiChatInput;
    setAiChatInput("");
    setAiChatHistory(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsAiLoading(true);

    try {
      // Generate context-aware prompt when editing existing pattern
      const currentPatternContext = isEditingWithAI ? `
CURRENT PATTERN CONTEXT:
- Pattern Name: "${currentPattern.name}"
- Current Format: ${generateTemplateString(currentPattern.components)}
- Example Output: ${generatePreviewId(currentPattern.components)}
- Components Breakdown:
${currentPattern.components.map((c, i) => `  ${i + 1}. ${getComponentLabel(c.type)}: ${c.type === 'static' ? `"${c.value}"` : c.type === 'year' ? `Format: ${c.format}` : c.type === 'sequence' ? `${c.length} digits, resets: ${c.resetOn}` : JSON.stringify(c)}`).join('\n')}
` : '';

      const editingInstructions = isEditingWithAI ? `
You are helping the user MODIFY their existing admission number pattern. The current pattern is already loaded above.

CRITICAL BEHAVIOR FOR EDITING:
- Start by acknowledging the current pattern and its structure.
- Ask the user in a friendly way: "Which part of this pattern would you like to change?"
- List the components clearly: "1. Prefix (SCT), 2. Separator (-), 3. Sequence (3 digits), etc."
- When the user says what they want to change, confirm the change and generate an updated JSON.
- Be helpful and conversational. Keep it simple.

EXAMPLE EDITING FLOW:
You: "I see your current pattern is ${generatePreviewId(currentPattern.components)}. It has ${currentPattern.components.length} parts. Which part would you like to modify?"
User: "Change the prefix to ADM"
You: "Got it! Changing the prefix from 'SCT' to 'ADM'. Your new format will be ADM-253-018/2025. Here's the updated configuration..." [Then output updated JSON]
` : `
You are an expert Admission Number Architect named "PatternBot". Your goal is to reverse-engineer a perfect admission number pattern from a User's sample.

CRITICAL BEHAVIOR:
- Be clever, analytical, and helpful.
- FIRST, ask the user to provide a *sample* admission number (e.g., "SCT/2024/001" or "ADM-123").
- If the user provides a sample, "DEBUNK" it: break it down into parts and explain what you see.
- Ask clarifying questions about specific sections if ambiguous.
- Once you understand the structure, generate the JSON.
- Keep responses short and focused. NO emojis.
`;

      const systemPrompt = `
${editingInstructions}
${currentPatternContext}

ANALYSIS GUIDE:
- "SCT", "ADM", "B" -> Likely 'static' text.
- "2024", "24", "2025" -> Likely 'year' component.
- "001", "0001", "123" -> Likely 'sequence' (check length).
- "/", "-", "." -> Likely 'separator' (static).

WHEN YOU HAVE ALL INFO:
- Output the JSON configuration block
- JSON format: { "name": "Pattern Name", "components": [{ "type": "static"|"year"|"sequence"|"lookup", "value"?: "...", "format"?: "YYYY"|"YY", "length"?: number, "resetOn"?: "year"|"never" }] }

AVAILABLE COMPONENT TYPES:
- 'static': Fixed text (Prefixes, separators like '/', '-')
- 'year': Academic year (YYYY or YY format)
- 'sequence': Auto-increment number. Props: length (e.g. 3 for 001), resetOn ('year' or 'never')
- 'lookup': Variable value like department codes

CONVERSATION SO FAR:
${aiChatHistory.map(m => `${m.role.toUpperCase()}: ${m.text}`).join('\n')}

USER'S LATEST MESSAGE: ${userMsg}
      `;

      const response = await generateAdmissionPattern(systemPrompt, schoolID || undefined);
      
      // Parse JSON from response
      let displayText = response;
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      let foundPattern = null;

      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.components && Array.isArray(parsed.components)) {
            foundPattern = parsed.components.map((c: any) => ({
              ...c,
              id: crypto.randomUUID(),
              key: `${c.type}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
            }));
            displayText = response.replace(jsonMatch[0], "").trim() 
              || "I've generated a pattern based on your requirements. Review it below and click 'Apply' to use it.";
          }
        } catch (e) {
          console.error("AI JSON Parse Error", e);
        }
      }

      setAiChatHistory(prev => [...prev, { role: 'model', text: displayText }]);
      if (foundPattern) {
        setAiGeneratedPattern(foundPattern);
      }

    } catch (error: any) {
      console.error("AI Error", error);
      const errorMsg = error.message || "Error connecting to AI service. Please try again.";
      setAiChatHistory(prev => [...prev, { role: 'model', text: errorMsg }]);
    } finally {
      setIsAiLoading(false);
      // Auto-scroll to bottom after AI responds
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };
  
  // Update component
  const updateComponent = (id: string, updates: Partial<ComponentConfig>) => {
    setCurrentPattern(prev => {
      const newComponents = prev.components.map(c =>
        c.id === id ? { ...c, ...updates } : c
      );
      return {
        ...prev,
        components: newComponents,
        template: generateTemplateString(newComponents)
      };
    });
  };
  
  // Remove component
  const removeComponent = (id: string) => {
    setCurrentPattern(prev => {
      const newComponents = prev.components.filter(c => c.id !== id);
      return {
        ...prev,
        components: newComponents,
        template: generateTemplateString(newComponents)
      };
    });
  };
  
  // Move component
  const moveComponent = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= currentPattern.components.length) return;
    
    setCurrentPattern(prev => {
      const newComponents = [...prev.components];
      const [removed] = newComponents.splice(fromIndex, 1);
      newComponents.splice(toIndex, 0, removed);
      return {
        ...prev,
        components: newComponents,
        template: generateTemplateString(newComponents)
      };
    });
  };
  
  // Save pattern
  const savePattern = async () => {
    if (!schoolID) {
      toast.error("No school connected");
      return;
    }
    
    if (!currentPattern.name || currentPattern.components.length === 0) {
      toast.error("Pattern must have a name and at least one component");
      return;
    }
    
    setSaving(true);
    try {
      const patternData = {
        ...currentPattern,
        template: generateTemplateString(currentPattern.components),
        updatedAt: new Date().toISOString()
      };
      
      if (currentPattern.id) {
        await updateDoc(doc(db, "schools", schoolID, "admissionPatterns", currentPattern.id), patternData);
        toast.success("Pattern updated");
      } else {
        const docRef = await addDoc(collection(db, "schools", schoolID, "admissionPatterns"), {
          ...patternData,
          createdAt: new Date().toISOString()
        });
        setCurrentPattern(prev => ({ ...prev, id: docRef.id }));
        toast.success("Pattern created");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save pattern");
    } finally {
      setSaving(false);
    }
  };
  
  // Test parsing
  const testParse = () => {
    if (!testInput) {
      toast.error("Enter a test ID");
      return;
    }
    
    const result = parseAdmissionNumber(testInput, currentPattern.components);
    setTestResult(result);
    
    if (result.success) {
      const next = generateNextId(result.components, currentPattern.components, 1);
      const prev = generateNextId(result.components, currentPattern.components, -1);
      setGeneratedNext(next);
      setGeneratedPrev(prev);
    } else {
      setGeneratedNext("");
      setGeneratedPrev("");
    }
  };
  
  // Import from samples
  const importFromSamples = () => {
    // Basic auto-detection - simplified version
    const samples = importSamples.split('\n').filter(s => s.trim());
    if (samples.length === 0) {
      toast.error("Enter at least one sample ID");
      return;
    }
    
    // Very basic pattern detection for demo
    const sample = samples[0].trim();
    const detectedComponents: ComponentConfig[] = [];
    
    // Check for year patterns
    const yearMatch = sample.match(/\b(19|20)\d{2}\b/);
    const shortYearMatch = sample.match(/\b\d{2}\b/);
    
    // Check for sequence patterns
    const seqMatch = sample.match(/\d{3,6}/);
    
    // Build basic pattern
    let remaining = sample;
    
    if (yearMatch) {
      const yearPos = sample.indexOf(yearMatch[0]);
      if (yearPos > 0) {
        detectedComponents.push({
          id: generateId(),
          key: 'prefix',
          type: 'static',
          value: sample.slice(0, yearPos)
        });
      }
      detectedComponents.push({
        id: generateId(),
        key: 'year',
        type: 'year',
        format: 'YYYY'
      });
      remaining = sample.slice(yearPos + 4);
    }
    
    if (remaining.length > 0) {
      // Check for delimiter + sequence
      const delimMatch = remaining.match(/^([^0-9]+)(\d+)/);
      if (delimMatch) {
        detectedComponents.push({
          id: generateId(),
          key: 'delim',
          type: 'static',
          value: delimMatch[1]
        });
        detectedComponents.push({
          id: generateId(),
          key: 'seq',
          type: 'sequence',
          length: delimMatch[2].length,
          min: 1,
          max: Math.pow(10, delimMatch[2].length) - 1,
          resetOn: 'year'
        });
      }
    }
    
    if (detectedComponents.length > 0) {
      setCurrentPattern(prev => ({
        ...prev,
        components: detectedComponents,
        template: generateTemplateString(detectedComponents)
      }));
      toast.success(`Detected ${detectedComponents.length} components`);
    } else {
      toast.error("Could not auto-detect pattern. Please configure manually.");
    }
    
    setShowImportDialog(false);
    setImportSamples("");
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
            {showAIDialog ? (
              /* ============== FULL PAGE AI PATTERN DESIGNER (Exact ChatbotPage UI Replica) ============== */
              <div className="flex flex-col flex-1 min-h-0 relative bg-white dark:bg-[#020817]">
                
                {/* Page Title Area - exact ChatbotPage style */}
                <div className="flex-none px-4 py-3 sm:px-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-[#020817]">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="mr-2" 
                      onClick={() => {
                        setShowAIDialog(false);
                        setAiChatHistory([]);
                        setAiGeneratedPattern(null);
                        setIsEditingWithAI(false);
                      }}
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <div className={`p-2 rounded-lg ${isEditingWithAI ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                      <Sparkles className={`w-5 h-5 ${isEditingWithAI ? 'text-purple-600 dark:text-purple-400' : 'text-blue-600 dark:text-blue-400'}`} />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                        {isEditingWithAI ? `Editing: ${currentPattern.name}` : 'AI Pattern Designer'}
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        {isEditingWithAI ? 'Tell me what you want to change' : 'Powered by Gemini AI'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages Area - exact ChatbotPage style */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-40 gap-6 scroll-smooth">
                  {aiChatHistory.length === 0 && !isAiLoading && (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-0 animate-in fade-in duration-500">
                      <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center mb-4">
                        <Bot className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        How can I help you today?
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-md text-sm">
                        I can reverse-engineer your admission number format. <br/>
                        Just type a sample like <strong>"SCT-253-019/2021"</strong> and I'll break it down for you!
                      </p>
                    </div>
                  )}

                  {aiChatHistory.map((msg, i) => (
                    <div
                      key={i}
                      className={cn(
                        "flex w-full gap-3 sm:gap-4 group mb-4",
                        msg.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      {/* AI Avatar */}
                      {msg.role !== "user" && (
                        <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-gray-200 dark:border-gray-700 flex-none">
                          <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                            <Bot className="h-5 w-5" />
                          </AvatarFallback>
                        </Avatar>
                      )}

                      {/* Message Bubble */}
                      <div className={cn(
                        "flex flex-col max-w-[85%] sm:max-w-[75%]",
                        msg.role === "user" ? "items-end" : "items-start"
                      )}>
                        <div className="flex items-end gap-2">
                          <div
                            className={cn(
                              "px-4 py-3 rounded-2xl text-sm sm:text-base shadow-sm whitespace-pre-wrap break-words",
                              msg.role === "user"
                                ? "bg-blue-600 text-white rounded-br-none"
                                : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-none"
                            )}
                          >
                            {msg.text}
                          </div>
                          
                          {/* Copy Button for AI */}
                          {msg.role === "model" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-8 w-8 rounded-full opacity-0 transition-opacity duration-200",
                                "group-hover:opacity-100 focus:opacity-100",
                                "md:opacity-0" 
                              )}
                              onClick={() => navigator.clipboard.writeText(msg.text)}
                              title="Copy response"
                            >
                              <Copy className="h-4 w-4 text-gray-500" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* User Avatar */}
                      {msg.role === "user" && (
                        <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-gray-200 dark:border-gray-700 flex-none">
                          <AvatarFallback className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                            <User className="h-5 w-5" />
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  ))}

                  {/* Typing Indicator */}
                  {isAiLoading && (
                    <div className="flex w-full gap-3 sm:gap-4">
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-gray-200 dark:border-gray-700 flex-none">
                        <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                          <Bot className="h-5 w-5" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3 rounded-2xl rounded-bl-none shadow-sm flex items-center gap-1">
                        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></span>
                      </div>
                    </div>
                  )}

                  {/* Generated Pattern Preview */}
                  {aiGeneratedPattern && (
                    <div className="border-2 border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/30 rounded-xl p-6 mt-4 max-w-md mx-auto">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-sm font-bold text-green-700 dark:text-green-300 uppercase tracking-wider">Generated Pattern</h4>
                        <span className="text-xs bg-white dark:bg-black px-2 py-1 rounded-full border shadow-sm font-mono">
                          {generateTemplateString(aiGeneratedPattern)}
                        </span>
                      </div>
                      <div className="text-2xl font-mono font-bold text-center py-4 text-green-900 dark:text-green-100 bg-white/60 dark:bg-black/30 rounded-lg border border-dashed border-green-300 dark:border-green-700 mb-4">
                        {generatePreviewId(aiGeneratedPattern)}
                      </div>
                      <Button 
                        className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white h-10" 
                        disabled={saving}
                        onClick={async () => {
                          if (!schoolID) {
                            toast.error("No school connected");
                            return;
                          }
                          
                          setSaving(true);
                          try {
                            // Prepare pattern data with isActive = true
                            const patternData: PatternDefinition = {
                              name: "AI Generated Pattern",
                              template: generateTemplateString(aiGeneratedPattern),
                              components: aiGeneratedPattern,
                              yearResetRule: 'calendar',
                              academicYearStart: { month: 9, day: 1 },
                              onOverflow: 'error',
                              isActive: true,
                              createdAt: new Date().toISOString(),
                              updatedAt: new Date().toISOString()
                            };
                            
                            // Deactivate any existing active patterns first
                            const existingActive = patterns.find(p => p.isActive);
                            if (existingActive && existingActive.id) {
                              await updateDoc(doc(db, "schools", schoolID, "admissionPatterns", existingActive.id), {
                                isActive: false
                              });
                            }
                            
                            // Save the new pattern
                            const docRef = await addDoc(collection(db, "schools", schoolID, "admissionPatterns"), patternData);
                            
                            // Update local state
                            setCurrentPattern({
                              ...patternData,
                              id: docRef.id
                            });
                            
                            setShowAIDialog(false);
                            setAiGeneratedPattern(null);
                            setAiChatHistory([]);
                            toast.success("Pattern saved and activated!");
                          } catch (error: any) {
                            console.error("Error saving pattern:", error);
                            toast.error(`Failed to save: ${error.message || 'Unknown error'}`);
                          } finally {
                            setSaving(false);
                          }
                        }}
                      >
                        {saving ? (
                          <>
                            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4" /> Save & Activate Pattern
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {/* Scroll anchor for auto-scroll */}
                  <div ref={chatEndRef} />
                  
                  {/* Spacer behind floating input */}
                  <div className="h-40 flex-none" />
                </div>

                {/* Input Area - exact ChatbotPage floating style */}
                <div className="absolute bottom-0 left-0 w-full z-20 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-[#020817] dark:via-[#020817]/95 dark:to-transparent pt-10 pb-6 px-4">
                  <div className="max-w-2xl mx-auto">
                    <form onSubmit={(e) => { e.preventDefault(); handleAISubmit(); }}>
                      <div className="relative flex items-center gap-2 bg-gray-100 dark:bg-gray-800/80 border border-transparent dark:border-gray-700/50 rounded-3xl p-1.5 px-3 shadow-sm focus-within:ring-1 focus-within:ring-white/20 transition-all">
                        <Textarea
                          value={aiChatInput}
                          onChange={(e) => setAiChatInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAISubmit();
                            }
                          }}
                          placeholder="Ask anything..."
                          className="min-h-[40px] max-h-[160px] w-full resize-none bg-transparent border-none focus-visible:ring-0 px-2 py-2 text-sm shadow-none placeholder:text-gray-500"
                          disabled={isAiLoading}
                          rows={1}
                        />
                        <Button
                          type="submit"
                          disabled={isAiLoading || !aiChatInput.trim()}
                          size="icon"
                          className={cn(
                            "h-8 w-8 rounded-full shrink-0 transition-all duration-200",
                            aiChatInput.trim() 
                              ? "bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 shadow-sm" 
                              : "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                          )}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </form>
                    <p className="text-center text-xs text-muted-foreground mt-3 font-medium opacity-70">
                      AI can make mistakes. Please verify the generated pattern.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <main className="flex-1 overflow-y-auto p-4 md:p-6">
                {showHelpPage ? (
              /* ============== FULL PAGE HELP DOCUMENTATION ============== */
              <div className="max-w-5xl mx-auto space-y-8">
                {/* Help Header */}
                <div className="flex items-center gap-4">
                  <Button 
                    variant="ghost" 
                    className="border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700" 
                    onClick={() => setShowHelpPage(false)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Setup
                  </Button>
                  <div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <HelpCircle className="h-6 w-6 text-primary" />
                      Admission Number System - Complete Documentation
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Everything you need to know about configuring admission number patterns
                    </p>
                  </div>
                </div>

                {/* Table of Contents */}
                <Card>
                  <CardHeader>
                    <CardTitle>Table of Contents</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <a href="#overview" className="text-sm text-primary hover:underline">1. Overview</a>
                      <a href="#philosophy" className="text-sm text-primary hover:underline">2. Design Philosophy</a>
                      <a href="#components" className="text-sm text-primary hover:underline">3. Component Types</a>
                      <a href="#pattern-syntax" className="text-sm text-primary hover:underline">4. Pattern Syntax</a>
                      <a href="#parsing" className="text-sm text-primary hover:underline">5. Parsing Algorithm</a>
                      <a href="#generation" className="text-sm text-primary hover:underline">6. Generation Logic</a>
                      <a href="#rollover" className="text-sm text-primary hover:underline">7. Rollover Rules</a>
                      <a href="#checksums" className="text-sm text-primary hover:underline">8. Checksum Algorithms</a>
                      <a href="#edge-cases" className="text-sm text-primary hover:underline">9. Edge Cases</a>
                      <a href="#examples" className="text-sm text-primary hover:underline">10. Worked Examples</a>
                    </div>
                  </CardContent>
                </Card>

                {/* 1. Overview */}
                <Card id="overview">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">1</span>
                      Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 m-1">
                    <p>
                      The Admission Number System is a highly configurable engine for generating, parsing, and validating student admission numbers. 
                      It supports patterns used by universities, polytechnics, examination bodies (JAMB, KNEC, WAEC), and modern ERP systems worldwide.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 rounded-lg bg-green-50/50 dark:bg-green-900/10 border border-green-200/50 dark:border-green-800/30 m-2">
                        <h4 className="font-semibold flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-600" /> Parse</h4>
                        <p className="text-sm text-muted-foreground mt-1">Break down any admission number into its components</p>
                      </div>
                      <div className="p-4 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200/50 dark:border-blue-800/30 m-2">
                        <h4 className="font-semibold flex items-center gap-2"><Play className="h-4 w-4 text-blue-600" /> Generate</h4>
                        <p className="text-sm text-muted-foreground mt-1">Create next, previous, or offset IDs automatically</p>
                      </div>
                      <div className="p-4 rounded-lg bg-purple-50/50 dark:bg-purple-900/10 border border-purple-200/50 dark:border-purple-800/30 m-2">
                        <h4 className="font-semibold flex items-center gap-2"><Shield className="h-4 w-4 text-purple-600" /> Validate</h4>
                        <p className="text-sm text-muted-foreground mt-1">Verify format, range, and checksum integrity</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Design Philosophy */}
                <Card id="philosophy">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">2</span>
                      Design Philosophy & Principles
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="m-1">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 dark:border-slate-700">
                            <th className="text-left py-2 px-3 font-semibold">Principle</th>
                            <th className="text-left py-2 px-3 font-semibold">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-slate-200 dark:border-slate-700">
                            <td className="py-2 px-3 font-medium">Pattern-First</td>
                            <td className="py-2 px-3 text-muted-foreground">The system is driven by a user-defined pattern template. All operations derive from this single source of truth.</td>
                          </tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700">
                            <td className="py-2 px-3 font-medium">Component-Based</td>
                            <td className="py-2 px-3 text-muted-foreground">Each pattern is a sequence of typed components. Components are independent, reusable, and composable.</td>
                          </tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700">
                            <td className="py-2 px-3 font-medium">Stateless Generation</td>
                            <td className="py-2 px-3 text-muted-foreground">Given a current ID and pattern, compute next/previous without external state beyond pattern definition.</td>
                          </tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700">
                            <td className="py-2 px-3 font-medium">Graceful Overflow</td>
                            <td className="py-2 px-3 text-muted-foreground">When a sequence exhausts its range, either carry to adjacent component (if allowed) or report error.</td>
                          </tr>
                          <tr>
                            <td className="py-2 px-3 font-medium">Extensible</td>
                            <td className="py-2 px-3 text-muted-foreground">New component types can be added without breaking existing patterns.</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* 3. Component Types */}
                <Card id="components">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">3</span>
                      Component Types (Detailed)
                    </CardTitle>
                    <CardDescription>Each component type has specific behavior for parsing and generation</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 m-1">
                    
                    {/* Static */}
                    <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 m-2">
                      <div className="flex items-center gap-2 font-semibold text-lg mb-2"><Type className="h-5 w-5" /> STATIC</div>
                      <p className="text-muted-foreground mb-3">Fixed literal text that never changes. Used for prefixes, suffixes, and delimiters.</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Parse:</span> Must match exactly or error</div>
                        <div><span className="font-medium">Generate:</span> Output as-is</div>
                      </div>
                      <div className="mt-3 p-2 bg-background rounded font-mono text-sm">
                        Examples: "SCT-", "/", "-", "UON/"
                      </div>
                    </div>

                    {/* Year */}
                    <div className="p-4 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 border border-blue-200/50 dark:border-blue-800/30 m-2">
                      <div className="flex items-center gap-2 font-semibold text-lg mb-2"><Calendar className="h-5 w-5" /> YEAR</div>
                      <p className="text-muted-foreground mb-3">Calendar or academic year. Triggers sequence reset when configured.</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Formats:</span> YYYY (2025) or YY (25)</div>
                        <div><span className="font-medium">Validation:</span> Must be in reasonable range (1950-2100)</div>
                      </div>
                      <div className="mt-3 p-2 bg-background rounded font-mono text-sm">
                        Template: {'{YEAR:YYYY}'} or {'{YEAR:YY}'}
                      </div>
                    </div>

                    {/* Semester */}
                    <div className="p-4 rounded-lg bg-purple-50/50 dark:bg-purple-900/10 border border-purple-200/50 dark:border-purple-800/30 m-2">
                      <div className="flex items-center gap-2 font-semibold text-lg mb-2"><Calendar className="h-5 w-5" /> SEMESTER</div>
                      <p className="text-muted-foreground mb-3">Semester or term number. Derived from academic calendar configuration.</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Values:</span> 1, 2 (or 3 for trimester)</div>
                        <div><span className="font-medium">Reset:</span> Can optionally reset sequence on change</div>
                      </div>
                      <div className="mt-3 p-2 bg-background rounded font-mono text-sm">
                        Template: {'{SEM}'}
                      </div>
                    </div>

                    {/* Sequence */}
                    <div className="p-4 rounded-lg bg-green-50/50 dark:bg-green-900/10 border border-green-200/50 dark:border-green-800/30 m-2">
                      <div className="flex items-center gap-2 font-semibold text-lg mb-2"><Hash className="h-5 w-5" /> SEQUENCE</div>
                      <p className="text-muted-foreground mb-3">Auto-incrementing numeric value with configurable range and padding.</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Config:</span> length, min, max, resetOn, carryTo</div>
                        <div><span className="font-medium">Padding:</span> Zero-padded to specified length</div>
                      </div>
                      <div className="mt-3 p-2 bg-background rounded font-mono text-sm">
                        Template: {'{SEQ:4}'} (4 digits), {'{SEQ:5:1:99999}'} (5 digits, 1-99999)
                      </div>
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Example:</span> 0001 → 0002 → ... → 9999 → overflow/reset
                      </div>
                    </div>

                    {/* Alpha Sequence */}
                    <div className="p-4 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-800/30 m-2">
                      <div className="flex items-center gap-2 font-semibold text-lg mb-2"><Type className="h-5 w-5" /> ALPHA-SEQUENCE</div>
                      <p className="text-muted-foreground mb-3">Letter-based incrementing sequence. Rightmost character increments first, carries left.</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Alphabets:</span> A-Z (26 chars), 0-9A-Z (36 chars), custom</div>
                        <div><span className="font-medium">Carry:</span> AZ → BA, ZZ → AAA (if extended)</div>
                      </div>
                      <div className="mt-3 p-2 bg-background rounded font-mono text-sm">
                        Template: {'{ALPHA:2:A-Z}'} → AA, AB, AC, ... AZ, BA, ... ZZ
                      </div>
                    </div>

                    {/* Checksum */}
                    <div className="p-4 rounded-lg bg-red-50/50 dark:bg-red-900/10 border border-red-200/50 dark:border-red-800/30 m-2">
                      <div className="flex items-center gap-2 font-semibold text-lg mb-2"><Shield className="h-5 w-5" /> CHECKSUM</div>
                      <p className="text-muted-foreground mb-3">Validation digit computed from preceding components. Detects typos and transcription errors.</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Algorithms:</span> Luhn, Mod10, Mod11, Verhoeff</div>
                        <div><span className="font-medium">Parse:</span> Verify computed checksum matches</div>
                      </div>
                      <div className="mt-3 p-2 bg-background rounded font-mono text-sm">
                        Template: {'{CHK:luhn}'} or {'{CHK:mod11}'}
                      </div>
                    </div>

                    {/* Lookup */}
                    <div className="p-4 rounded-lg bg-cyan-50/50 dark:bg-cyan-900/10 border border-cyan-200/50 dark:border-cyan-800/30 m-2">
                      <div className="flex items-center gap-2 font-semibold text-lg mb-2"><List className="h-5 w-5" /> LOOKUP</div>
                      <p className="text-muted-foreground mb-3">Department, faculty, or program codes from a predefined list.</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Validation:</span> Must exist in configured list</div>
                        <div><span className="font-medium">Parse:</span> Matches longest code first</div>
                      </div>
                      <div className="mt-3 p-2 bg-background rounded font-mono text-sm">
                        Values: ENG, MED, LAW, CHEM, PHYS, BIOL, MATH
                      </div>
                    </div>

                  </CardContent>
                </Card>

                {/* 4. Pattern Syntax */}
                <Card id="pattern-syntax">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">4</span>
                      Pattern Syntax / Template DSL
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="m-1">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                            <th className="text-left py-2 px-3 font-semibold">Syntax</th>
                            <th className="text-left py-2 px-3 font-semibold">Description</th>
                            <th className="text-left py-2 px-3 font-semibold">Example Output</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{STATIC:value}'}</td><td className="py-2 px-3">Literal text</td><td className="py-2 px-3 font-mono">SCT-</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{YEAR:YYYY}'}</td><td className="py-2 px-3">4-digit year</td><td className="py-2 px-3 font-mono">2025</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{YEAR:YY}'}</td><td className="py-2 px-3">2-digit year</td><td className="py-2 px-3 font-mono">25</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{SEM}'}</td><td className="py-2 px-3">Semester (1-3)</td><td className="py-2 px-3 font-mono">1</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{SEQ:4}'}</td><td className="py-2 px-3">4-digit sequence</td><td className="py-2 px-3 font-mono">0001</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{SEQ:5:1:99999}'}</td><td className="py-2 px-3">5-digit, min 1, max 99999</td><td className="py-2 px-3 font-mono">00001</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{ALPHA:2:A-Z}'}</td><td className="py-2 px-3">2-char letters only</td><td className="py-2 px-3 font-mono">AA</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{ALPHA:3:0-9A-Z}'}</td><td className="py-2 px-3">3-char alphanumeric</td><td className="py-2 px-3 font-mono">A00</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3 font-mono">{'{CHK:luhn}'}</td><td className="py-2 px-3">Luhn check digit</td><td className="py-2 px-3 font-mono">7</td></tr>
                          <tr><td className="py-2 px-3 font-mono">{'{DEPT}'}</td><td className="py-2 px-3">Lookup from list</td><td className="py-2 px-3 font-mono">ENG</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* 5. Parsing Algorithm */}
                <Card id="parsing">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">5</span>
                      Parsing Algorithm
                    </CardTitle>
                    <CardDescription>Step-by-step logic for breaking down an admission number</CardDescription>
                  </CardHeader>
                  <CardContent className="m-1">
                    <ol className="list-decimal list-inside space-y-2">
                      <li><strong>Initialize cursor = 0</strong>, result = empty object</li>
                      <li><strong>For each component</strong> in pattern.components:
                        <ul className="list-disc list-inside ml-6 mt-1 text-muted-foreground">
                          <li>STATIC: Extract expected length, compare. If mismatch → error</li>
                          <li>YEAR: Extract 2 or 4 digits, parse integer, validate range</li>
                          <li>SEQUENCE: Extract N digits, parse integer, check min/max bounds</li>
                          <li>ALPHA: Extract N characters, validate against alphabet</li>
                          <li>CHECKSUM: Extract 1 digit, compute expected, compare</li>
                          <li>LOOKUP: Match against list (longest first), error if no match</li>
                        </ul>
                      </li>
                      <li><strong>If cursor ≠ input.length</strong> → error (extra characters)</li>
                      <li><strong>Return parsed result</strong> with key-value pairs for each component</li>
                    </ol>
                  </CardContent>
                </Card>

                {/* 6. Generation Algorithm */}
                <Card id="generation">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">6</span>
                      Next / Previous / Offset Generation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="m-1">
                    <ol className="list-decimal list-inside space-y-2">
                      <li><strong>Clone parsed values</strong> into working copy</li>
                      <li><strong>Find rightmost incrementable component</strong> (SEQUENCE or ALPHA-SEQUENCE)</li>
                      <li><strong>Apply offset</strong> to that component:
                        <ul className="list-disc list-inside ml-6 mt-1 text-muted-foreground">
                          <li>new_value = current + offset</li>
                          <li>If new_value {'>'} max: handle overflow (error/carry/wrap)</li>
                          <li>If new_value {'<'} min: handle underflow (borrow)</li>
                        </ul>
                      </li>
                      <li><strong>Handle year/semester rollover</strong> if configured</li>
                      <li><strong>Recalculate checksum</strong> if present</li>
                      <li><strong>Format output</strong> by iterating components and applying padding</li>
                    </ol>
                    <div className="mt-4 p-3 rounded-lg bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 m-2">
                      <div className="font-medium mb-2">Shortcuts:</div>
                      <code className="text-sm">next(current) = generate(current, +1)</code><br/>
                      <code className="text-sm">previous(current) = generate(current, -1)</code>
                    </div>
                  </CardContent>
                </Card>

                {/* 7. Rollover Rules */}
                <Card id="rollover">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">7</span>
                      Rollover, Carry & Reset Rules
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 m-1">
                    <div>
                      <h4 className="font-semibold mb-2">Year Rollover</h4>
                      <ul className="list-disc list-inside text-muted-foreground">
                        <li><strong>When:</strong> Current date crosses year boundary (calendar/academic/fiscal)</li>
                        <li><strong>Action:</strong> Reset all sequences with resetOn: 'year' to their min value</li>
                        <li><strong>Year component:</strong> Updates to new year</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Semester Rollover</h4>
                      <ul className="list-disc list-inside text-muted-foreground">
                        <li><strong>When:</strong> Semester changes based on academic calendar</li>
                        <li><strong>Action:</strong> Reset sequences with resetOn: 'semester'</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Sequence Overflow → Carry</h4>
                      <div className="p-3 rounded-lg bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 font-mono text-sm mt-2 m-2">
                        Pattern: {'{ALPHA:2:A-Z}{SEQ:3}'} (AA001 to ZZ999)<br/>
                        Current: AZ999<br/>
                        Next: BA001<br/><br/>
                        Logic: SEQ 999+1 = overflow → wrap to 001, carry +1 to ALPHA → AZ+1 = BA
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* 8. Checksum Algorithms */}
                <Card id="checksums">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">8</span>
                      Checksum Algorithms
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 m-1">
                    <div>
                      <h4 className="font-semibold mb-2">Luhn Algorithm</h4>
                      <ol className="list-decimal list-inside text-sm text-muted-foreground">
                        <li>From rightmost digit, double every second digit</li>
                        <li>If doubled value {'>'} 9, subtract 9</li>
                        <li>Sum all digits</li>
                        <li>Check digit = (10 - (sum % 10)) % 10</li>
                      </ol>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Modulo-11</h4>
                      <ol className="list-decimal list-inside text-sm text-muted-foreground">
                        <li>Multiply each position by weight (2,3,4,5,6,7,2,3,... from right)</li>
                        <li>Sum products</li>
                        <li>Remainder = sum % 11</li>
                        <li>Check digit = 11 - remainder (if 10 → 'X', if 11 → '0')</li>
                      </ol>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Modulo-10 (Simple)</h4>
                      <ol className="list-decimal list-inside text-sm text-muted-foreground">
                        <li>Sum all digits</li>
                        <li>Check digit = (10 - (sum % 10)) % 10</li>
                      </ol>
                    </div>
                  </CardContent>
                </Card>

                {/* 9. Edge Cases */}
                <Card id="edge-cases">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">9</span>
                      Edge Cases & Special Situations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="m-1">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                            <th className="text-left py-2 px-3 font-semibold">#</th>
                            <th className="text-left py-2 px-3 font-semibold">Case</th>
                            <th className="text-left py-2 px-3 font-semibold">Handling</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">1</td><td className="py-2 px-3">First ID of year</td><td className="py-2 px-3 text-muted-foreground">Sequence starts at min (001)</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">2</td><td className="py-2 px-3">Year boundary during generation</td><td className="py-2 px-3 text-muted-foreground">Detect year ≠ parsed year, may reset seq</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">3</td><td className="py-2 px-3">Semester mid-year</td><td className="py-2 px-3 text-muted-foreground">Use academic calendar config</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">4</td><td className="py-2 px-3">Fiscal year (Apr-Mar)</td><td className="py-2 px-3 text-muted-foreground">fiscalYearStart: {'{ month: 4, day: 1 }'}</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">5</td><td className="py-2 px-3">Leading zeros stripped</td><td className="py-2 px-3 text-muted-foreground">Always store as string, reapply padding</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">6</td><td className="py-2 px-3">Unknown department code</td><td className="py-2 px-3 text-muted-foreground">Return parse error with suggestion</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">7</td><td className="py-2 px-3">Checksum mismatch</td><td className="py-2 px-3 text-muted-foreground">Return error, optionally suggest correction</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">8</td><td className="py-2 px-3">Negative offset past min</td><td className="py-2 px-3 text-muted-foreground">Underflow error or borrow from higher</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">9</td><td className="py-2 px-3">Alpha sequence exhausted</td><td className="py-2 px-3 text-muted-foreground">ZZ + 1 with no carry target = error</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">10</td><td className="py-2 px-3">Mixed delimiters</td><td className="py-2 px-3 text-muted-foreground">Pattern explicitly defines each as static</td></tr>
                          <tr className="border-b border-slate-200 dark:border-slate-700"><td className="py-2 px-3">11</td><td className="py-2 px-3">Optional components</td><td className="py-2 px-3 text-muted-foreground">Mark as optional, allow empty during parse</td></tr>
                          <tr><td className="py-2 px-3">12</td><td className="py-2 px-3">Legacy format migration</td><td className="py-2 px-3 text-muted-foreground">Support multiple patterns, try each until success</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* 10. Worked Examples */}
                <Card id="examples">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs">10</span>
                      Worked Examples
                    </CardTitle>
                    <CardDescription>Real-world patterns with parse and generate examples</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6 m-1">
                    
                    <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/10 m-2">
                      <h4 className="font-semibold mb-2">Example 1: 2024/00123</h4>
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Template:</span> <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{'{YEAR:YYYY}/{SEQ:5}'}</code></div>
                        <div><span className="font-medium">Input:</span> <code>2024/00123</code></div>
                        <div><span className="font-medium">Next:</span> <code className="text-green-600">2024/00124</code></div>
                        <div><span className="font-medium">Previous:</span> <code className="text-blue-600">2024/00122</code></div>
                        <div><span className="font-medium">Year rollover:</span> 2024/99999 → 2025/00001</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/10 m-2">
                      <h4 className="font-semibold mb-2">Example 2: SCT-253-019/2021</h4>
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Template:</span> <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">SCT-{'{SEQ:3}'}-{'{SEQ:3}'}/{'{YEAR:YYYY}'}</code></div>
                        <div><span className="font-medium">Config:</span> First SEQ carries to second SEQ</div>
                        <div><span className="font-medium">Next:</span> <code className="text-green-600">SCT-253-020/2021</code></div>
                        <div><span className="font-medium">Overflow:</span> SCT-253-999/2021 → SCT-254-001/2021</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/10 m-2">
                      <h4 className="font-semibold mb-2">Example 3: ENG/A001/2023</h4>
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Template:</span> <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{'{DEPT}'}/{'{ALPHA:1}'}{'{SEQ:3}'}/{'{YEAR:YYYY}'}</code></div>
                        <div><span className="font-medium">Next:</span> <code className="text-green-600">ENG/A002/2023</code></div>
                        <div><span className="font-medium">At limit:</span> ENG/A999/2023 → ENG/B001/2023</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/10 m-2">
                      <h4 className="font-semibold mb-2">Example 4: AA001 → Alphanumeric Carry</h4>
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Template:</span> <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{'{ALPHA:2:A-Z}{SEQ:3}'}</code></div>
                        <div><span className="font-medium">Input:</span> <code>AZ999</code></div>
                        <div><span className="font-medium">Next:</span> <code className="text-green-600">BA001</code></div>
                        <div><span className="font-medium">Logic:</span> SEQ overflows → reset to 001, carry to ALPHA → AZ → BA</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/10 m-2">
                      <h4 className="font-semibold mb-2">Example 5: 2024-12345-7 (With Luhn Checksum)</h4>
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Template:</span> <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{'{YEAR:YYYY}'}-{'{SEQ:5}'}-{'{CHK:luhn}'}</code></div>
                        <div><span className="font-medium">Input:</span> <code>2024-12345-7</code></div>
                        <div><span className="font-medium">Verify:</span> Luhn of "202412345" = 7 ✓</div>
                        <div><span className="font-medium">Next:</span> <code className="text-green-600">2024-12346-4</code> (checksum recalculated)</div>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/10 m-2">
                      <h4 className="font-semibold mb-2">Example 6: UON/2025/CHEM/045</h4>
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Template:</span> <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">UON/{'{YEAR:YYYY}'}/{'{DEPT}'}/{'{SEQ:3}'}</code></div>
                        <div><span className="font-medium">DEPT values:</span> CHEM, PHYS, MATH, BIOL, ...</div>
                        <div><span className="font-medium">Next:</span> <code className="text-green-600">UON/2025/CHEM/046</code></div>
                        <div><span className="font-medium">Overflow:</span> Error (dept doesn't auto-increment)</div>
                      </div>
                    </div>

                  </CardContent>
                </Card>

                {/* Back to Setup Button */}
                <div className="flex justify-center py-8">
                  <Button size="lg" onClick={() => setShowHelpPage(false)}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Admission Number Setup
                  </Button>
                </div>

              </div>
            ) : (
            /* ============== MAIN SETUP UI ============== */
            <div className="max-w-6xl mx-auto space-y-6">
              
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <Button 
                    variant="ghost" 
                    className="border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700" 
                    onClick={() => navigate('/dashboard/registration-management')}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                  <div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <Settings2 className="h-6 w-6 text-primary" />
                      Admission Number Setup
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Configure admission number format, parsing, and generation
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Import Pattern
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAIDialog(true)}
                    className="gap-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950"
                  >
                    <Sparkles className="h-4 w-4" />
                    Create with AI
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowHelpPage(true)} title="Help & Documentation">
                    <HelpCircle className="h-5 w-5 text-muted-foreground" />
                  </Button>
                  <Button onClick={() => setShowSaveConfirm(true)} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Pattern
                  </Button>
                </div>
              </div>
              
              {/* Current Scheme Display - Always show with toggle */}
              {(patterns.length > 0 || currentPattern.components.length > 0) && (
                <Card className={currentPattern.isActive 
                  ? "bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800 shadow-sm"
                  : "bg-slate-50/50 dark:bg-slate-900/10 border-slate-200 dark:border-slate-700 shadow-sm"
                }>
                  <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-4">
                    <div>
                      <h3 className={`text-sm font-medium flex items-center gap-2 mb-1 ${
                        currentPattern.isActive 
                          ? "text-green-800 dark:text-green-300" 
                          : "text-slate-600 dark:text-slate-400"
                      }`}>
                        {currentPattern.isActive ? (
                          <><CheckCircle2 className="h-4 w-4" /> Current Active Scheme</>
                        ) : (
                          <><Settings2 className="h-4 w-4" /> Current Scheme (Inactive)</>
                        )}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className={`text-2xl font-mono font-bold ${
                          currentPattern.isActive 
                            ? "text-green-900 dark:text-green-100" 
                            : "text-slate-900 dark:text-slate-100"
                        }`}>
                          {generatePreviewId(currentPattern.components)}
                        </span>
                        <div className="flex items-center gap-2">
                           <Badge variant="outline" className={currentPattern.isActive 
                             ? "bg-white/50 dark:bg-black/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                             : "bg-white/50 dark:bg-black/20"
                           }>
                             {currentPattern.name}
                           </Badge>
                           <span className="text-xs text-muted-foreground font-mono">
                             {generateTemplateString(currentPattern.components)}
                           </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className={`flex items-center gap-3 p-2 px-3 rounded-lg border ${
                        currentPattern.isActive 
                          ? "bg-white/60 dark:bg-black/20 border-green-200 dark:border-green-700"
                          : "bg-white/60 dark:bg-black/20 border-slate-200 dark:border-slate-700"
                      }`}>
                        <div className="text-xs">
                          <span className={`font-medium ${currentPattern.isActive ? "text-green-800 dark:text-green-200" : "text-slate-600 dark:text-slate-400"}`}>
                            Auto-implement
                          </span>
                        </div>
                        <Switch
                          checked={currentPattern.isActive}
                          disabled={saving}
                          onCheckedChange={async (checked) => {
                            if (checked) {
                              // Show dialog to ask about sequence start
                              setShowSequenceDialog(true);
                            } else {
                              // Toggle OFF - save to Firebase
                              if (!schoolID || !currentPattern.id) {
                                setCurrentPattern(prev => ({ ...prev, isActive: false }));
                                return;
                              }
                              
                              setSaving(true);
                              try {
                                await updateDoc(doc(db, "schools", schoolID, "admissionPatterns", currentPattern.id), {
                                  isActive: false,
                                  updatedAt: new Date().toISOString()
                                });
                                setCurrentPattern(prev => ({ ...prev, isActive: false }));
                                toast.success("Deactivated");
                              } catch (error: any) {
                                console.error("Error:", error);
                                toast.error("Failed to save");
                              } finally {
                                setSaving(false);
                              }
                            }
                          }}
                        />
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          setActiveTab("builder");
                          setIsEditing(true);
                        }}
                      >
                        Edit Scheme
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="shrink-0 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                        onClick={() => {
                          setPatternToDelete(currentPattern);
                          setShowDeleteConfirm(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Main Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="builder">Pattern Builder</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                  <TabsTrigger value="test">Test & Preview</TabsTrigger>
                </TabsList>
                
                {/* Builder Tab */}
                <TabsContent value="builder" className="space-y-4 mt-4">
                  {/* Editing Mode Banner */}
                  {isEditing && (
                    <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 border-l-4 border-l-blue-500">
                      <CardContent className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 bg-blue-100 dark:bg-blue-800/50 rounded-full">
                            <Settings2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <span className="font-medium text-blue-900 dark:text-blue-100">Editing Mode</span>
                            <span className="text-sm text-blue-700 dark:text-blue-300 ml-2">— {currentPattern.name}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-800 bg-purple-50 dark:bg-purple-900/20"
                            onClick={() => {
                              setIsEditingWithAI(true);
                              setShowAIDialog(true);
                              setAiChatHistory([]);
                              setAiGeneratedPattern(null);
                              // Pre-populate with a context-aware greeting
                              setTimeout(() => {
                                setAiChatHistory([{
                                  role: 'model',
                                  text: `I see you're editing "${currentPattern.name}" which currently generates numbers like: ${generatePreviewId(currentPattern.components)}\n\nYour pattern has ${currentPattern.components.length} parts:\n${currentPattern.components.map((c, i) => `${i + 1}. ${getComponentLabel(c.type)}${c.type === 'static' ? ` ("${c.value}")` : ''}`).join('\n')}\n\nWhich part would you like to change? Or describe what you'd like the new format to look like.`
                                }]);
                              }, 100);
                            }}
                          >
                            <Sparkles className="h-4 w-4 mr-1" /> Edit with AI
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800"
                            onClick={() => {
                              setIsEditing(false);
                              toast.info("Exited editing mode");
                            }}
                          >
                            <X className="h-4 w-4 mr-1" /> Cancel Editing
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    
                    {/* Component List */}
                    <div className="lg:col-span-2">
                      <Card>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-lg">Pattern Components</CardTitle>
                              <CardDescription>Drag to reorder, click to edit</CardDescription>
                            </div>
                            <Button size="sm" onClick={() => setShowAddComponent(true)}>
                              <Plus className="h-4 w-4 mr-1" />
                              Add Component
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {currentPattern.components.length === 0 ? (
                            <div className="text-center py-12 border-2 border-dashed rounded-lg bg-slate-50 dark:bg-slate-800/50">
                              <Hash className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                              <p className="text-muted-foreground">No components yet</p>
                              <p className="text-sm text-gray-400 mt-1">Click "Add Component" to start building your pattern</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {currentPattern.components.map((comp, index) => (
                                <div
                                  key={comp.id}
                                  className={`flex items-center gap-3 p-3 rounded-lg border ${getComponentColor(comp.type)}`}
                                >
                                  <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveComponent(index, index - 1)} disabled={index === 0}>
                                      <ChevronLeft className="h-3 w-3" />
                                    </Button>
                                    <GripVertical className="h-4 w-4 text-gray-400" />
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveComponent(index, index + 1)} disabled={index === currentPattern.components.length - 1}>
                                      <ChevronRight className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  
                                  <div className="flex items-center gap-2 flex-1">
                                    {getComponentIcon(comp.type)}
                                    <span className="font-medium">{getComponentLabel(comp.type)}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {comp.type === 'static' && comp.value}
                                      {comp.type === 'year' && comp.format}
                                      {comp.type === 'sequence' && `${comp.length} digits`}
                                      {comp.type === 'alpha-sequence' && `${comp.length} chars`}
                                      {comp.type === 'checksum' && comp.checksumAlg}
                                      {comp.type === 'lookup' && `${comp.lookupValues?.length || 0} values`}
                                    </Badge>
                                  </div>
                                  
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingComponent(comp)}>
                                      <Settings2 className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => removeComponent(comp.id)}>
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Preview Panel */}
                    <div className="space-y-4">
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg">Live Preview</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label className="text-xs text-muted-foreground">Template Syntax</Label>
                            <div className="font-mono text-sm bg-slate-100 dark:bg-slate-800 p-2 rounded mt-1 break-all">
                              {currentPattern.template || '(empty)'}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Example Output</Label>
                            <div className="font-mono text-lg font-bold bg-gradient-to-r from-primary/10 to-primary/5 p-3 rounded mt-1 text-center border border-primary/20">
                              {generatePreviewId(currentPattern.components) || '---'}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Input
                              value={currentPattern.name}
                              onChange={e => setCurrentPattern(prev => ({ ...prev, name: e.target.value }))}
                              className="font-semibold"
                              placeholder="Pattern Name"
                            />
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Badge variant={currentPattern.isActive ? "default" : "secondary"}>
                            {currentPattern.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Settings Tab */}
                <TabsContent value="settings" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Year Reset Rules</CardTitle>
                        <CardDescription>How sequences reset at year boundaries</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Reset Type</Label>
                          <Select 
                            value={currentPattern.yearResetRule} 
                            onValueChange={(v: YearResetRule) => setCurrentPattern(prev => ({ ...prev, yearResetRule: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="calendar">Calendar Year (Jan 1)</SelectItem>
                              <SelectItem value="academic">Academic Year</SelectItem>
                              <SelectItem value="fiscal">Fiscal Year</SelectItem>
                              <SelectItem value="none">Never Reset</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {currentPattern.yearResetRule === 'academic' && (
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-2">
                              <Label>Start Month</Label>
                              <Select 
                                value={String(currentPattern.academicYearStart.month)} 
                                onValueChange={v => setCurrentPattern(prev => ({ 
                                  ...prev, 
                                  academicYearStart: { ...prev.academicYearStart, month: parseInt(v) }
                                }))}
                              >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                                    <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Start Day</Label>
                              <Input 
                                type="number" 
                                min={1} 
                                max={31}
                                value={currentPattern.academicYearStart.day}
                                onChange={e => setCurrentPattern(prev => ({ 
                                  ...prev, 
                                  academicYearStart: { ...prev.academicYearStart, day: parseInt(e.target.value) || 1 }
                                }))}
                              />
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle>Overflow Behavior</CardTitle>
                        <CardDescription>What happens when a sequence exceeds its maximum</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>On Overflow</Label>
                          <Select 
                            value={currentPattern.onOverflow} 
                            onValueChange={(v: OverflowBehavior) => setCurrentPattern(prev => ({ ...prev, onOverflow: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="error">Show Error</SelectItem>
                              <SelectItem value="carry">Carry to Next Component</SelectItem>
                              <SelectItem value="wrap">Wrap to Minimum</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="text-sm text-muted-foreground p-3 bg-slate-50 dark:bg-slate-800 rounded">
                          {currentPattern.onOverflow === 'error' && "When sequence reaches max, an error will be shown and generation will stop."}
                          {currentPattern.onOverflow === 'carry' && "When sequence reaches max, it resets to min and increments the previous component."}
                          {currentPattern.onOverflow === 'wrap' && "When sequence reaches max, it wraps to min without affecting other components."}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
                
                {/* Test Tab */}
                <TabsContent value="test" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Test Parser</CardTitle>
                        <CardDescription>Enter an admission number to parse and validate</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex gap-2">
                          <Input 
                            placeholder="Enter admission number..."
                            value={testInput}
                            onChange={e => setTestInput(e.target.value)}
                            className="font-mono"
                          />
                          <Button onClick={testParse}>
                            <Play className="h-4 w-4 mr-1" />
                            Parse
                          </Button>
                        </div>
                        
                        {testResult && (
                          <div className={`p-4 rounded-lg border ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                            <div className="flex items-center gap-2 mb-2">
                              {testResult.success ? (
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                              ) : (
                                <AlertCircle className="h-5 w-5 text-red-600" />
                              )}
                              <span className={`font-medium ${testResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                                {testResult.success ? 'Valid Format' : 'Invalid Format'}
                              </span>
                            </div>
                            
                            {testResult.success ? (
                              <div className="space-y-2">
                                {testResult.components.map((c, i) => (
                                  <div key={i} className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{c.key}:</span>
                                    <span className="font-mono">{String(c.value)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-red-600 dark:text-red-400">{testResult.error}</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle>Generate Sequence</CardTitle>
                        <CardDescription>Preview next and previous numbers</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {testResult?.success ? (
                          <>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-muted-foreground">Previous (-1)</Label>
                                <div className="font-mono text-lg bg-slate-100 dark:bg-slate-800 p-3 rounded mt-1 flex items-center justify-between">
                                  {generatedPrev}
                                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => navigator.clipboard.writeText(generatedPrev)}>
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Next (+1)</Label>
                                <div className="font-mono text-lg bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-3 rounded mt-1 flex items-center justify-between border border-green-200 dark:border-green-800">
                                  {generatedNext}
                                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => navigator.clipboard.writeText(generatedNext)}>
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <Hash className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Parse a valid ID first to generate sequence</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
              
            </div>
            )}
          </main>
          )}
        </div>
      </div>
      
      {/* Add Component Dialog */}
      <Dialog open={showAddComponent} onOpenChange={setShowAddComponent}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Component</DialogTitle>
            <DialogDescription>Select a component type to add to your pattern</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-4">
            {(['static', 'year', 'semester', 'sequence', 'alpha-sequence', 'checksum', 'lookup'] as ComponentType[]).map(type => (
              <Button
                key={type}
                variant="outline"
                className="h-auto py-4 flex-col gap-2"
                onClick={() => addComponent(type)}
              >
                {getComponentIcon(type)}
                <span className="text-xs">{getComponentLabel(type)}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Component Dialog */}
      <Dialog open={!!editingComponent} onOpenChange={() => setEditingComponent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editingComponent && getComponentLabel(editingComponent.type)}</DialogTitle>
          </DialogHeader>
          {editingComponent && (
            <div className="space-y-4 py-4">
              {editingComponent.type === 'static' && (
                <div className="space-y-2">
                  <Label>Value</Label>
                  <Input 
                    value={editingComponent.value || ''}
                    onChange={e => {
                      const newVal = e.target.value;
                      updateComponent(editingComponent.id, { value: newVal });
                      setEditingComponent(prev => prev ? { ...prev, value: newVal } : null);
                    }}
                    placeholder="e.g., -, /, SCT"
                  />
                </div>
              )}
              
              {editingComponent.type === 'year' && (
                <div className="space-y-2">
                  <Label>Format</Label>
                  <Select 
                    value={editingComponent.format || 'YYYY'}
                    onValueChange={v => {
                      updateComponent(editingComponent.id, { format: v as YearFormat });
                      setEditingComponent(prev => prev ? { ...prev, format: v as YearFormat } : null);
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="YYYY">4-digit (2025)</SelectItem>
                      <SelectItem value="YY">2-digit (25)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {editingComponent.type === 'sequence' && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-2">
                      <Label>Length</Label>
                      <Input 
                        type="number"
                        min={1}
                        max={10}
                        value={editingComponent.length || 4}
                        onChange={e => {
                          const len = parseInt(e.target.value) || 4;
                          updateComponent(editingComponent.id, { length: len });
                          setEditingComponent(prev => prev ? { ...prev, length: len } : null);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Min</Label>
                      <Input 
                        type="number"
                        value={editingComponent.min ?? 1}
                        onChange={e => {
                          const min = parseInt(e.target.value) || 1;
                          updateComponent(editingComponent.id, { min });
                          setEditingComponent(prev => prev ? { ...prev, min } : null);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max</Label>
                      <Input 
                        type="number"
                        value={editingComponent.max ?? 9999}
                        onChange={e => {
                          const max = parseInt(e.target.value) || 9999;
                          updateComponent(editingComponent.id, { max });
                          setEditingComponent(prev => prev ? { ...prev, max } : null);
                        }}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Reset On</Label>
                    <Select 
                      value={editingComponent.resetOn || 'year'}
                      onValueChange={v => {
                        updateComponent(editingComponent.id, { resetOn: v as ResetOn });
                        setEditingComponent(prev => prev ? { ...prev, resetOn: v as ResetOn } : null);
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="year">Year Change</SelectItem>
                        <SelectItem value="semester">Semester Change</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {editingComponent.type === 'alpha-sequence' && (
                <>
                  <div className="space-y-2">
                    <Label>Length</Label>
                    <Input 
                      type="number"
                      min={1}
                      max={5}
                      value={editingComponent.length || 2}
                      onChange={e => {
                        const len = parseInt(e.target.value) || 2;
                        updateComponent(editingComponent.id, { length: len });
                        setEditingComponent(prev => prev ? { ...prev, length: len } : null);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Alphabet</Label>
                    <Select 
                      value={editingComponent.alphabet || 'A-Z'}
                      onValueChange={v => {
                        updateComponent(editingComponent.id, { alphabet: v });
                        setEditingComponent(prev => prev ? { ...prev, alphabet: v } : null);
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A-Z">Letters only (A-Z)</SelectItem>
                        <SelectItem value="0-9A-Z">Alphanumeric (0-9A-Z)</SelectItem>
                        <SelectItem value="0-9">Digits only (0-9)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {editingComponent.type === 'checksum' && (
                <div className="space-y-2">
                  <Label>Algorithm</Label>
                  <Select 
                    value={editingComponent.checksumAlg || 'luhn'}
                    onValueChange={v => {
                      updateComponent(editingComponent.id, { checksumAlg: v as ChecksumAlgorithm });
                      setEditingComponent(prev => prev ? { ...prev, checksumAlg: v as ChecksumAlgorithm } : null);
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="luhn">Luhn Algorithm</SelectItem>
                      <SelectItem value="mod10">Modulo 10</SelectItem>
                      <SelectItem value="mod11">Modulo 11</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {editingComponent.type === 'lookup' && (
                <div className="space-y-2">
                  <Label>Values (one per line)</Label>
                  <textarea 
                    className="w-full h-32 p-2 border rounded-md bg-background text-sm font-mono"
                    value={(editingComponent.lookupValues || []).join('\n')}
                    onChange={e => {
                      const values = e.target.value.split('\n').filter(s => s.trim());
                      updateComponent(editingComponent.id, { lookupValues: values });
                      setEditingComponent(prev => prev ? { ...prev, lookupValues: values } : null);
                    }}
                    placeholder="ENG&#10;MED&#10;LAW"
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setEditingComponent(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Auto-Detect Pattern
            </DialogTitle>
            <DialogDescription>
              Paste sample admission numbers to auto-detect the pattern structure
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <textarea 
              className="w-full h-40 p-3 border rounded-md bg-background text-sm font-mono"
              value={importSamples}
              onChange={e => setImportSamples(e.target.value)}
              placeholder="2024/00123&#10;2024/00124&#10;2024/00125"
            />
            <p className="text-xs text-muted-foreground">
              Enter at least 3-5 sample IDs for better detection accuracy
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
            <Button onClick={importFromSamples}>
              <Sparkles className="h-4 w-4 mr-2" />
              Detect Pattern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Save Confirmation Dialog */}
      <Dialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-primary" />
              Confirm Save Pattern
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to save this admission number pattern?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm font-medium">{currentPattern.name}</p>
              <p className="text-xs text-muted-foreground font-mono mt-1">{currentPattern.template || '(no template)'}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              This pattern will be used to generate and validate admission numbers for new students.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveConfirm(false)}>Cancel</Button>
            <Button onClick={() => { setShowSaveConfirm(false); savePattern(); }}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Confirm Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sequence Start Dialog */}
      <Dialog open={showSequenceDialog} onOpenChange={setShowSequenceDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base flex items-center gap-2">
              <Hash className="h-4 w-4 text-primary" />
              Activate Scheme
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            {/* Preview of pattern */}
            {(() => {
              const sequenceComp = currentPattern.components.find(c => c.type === 'sequence');
              
              const getPreview = (seqValue: number) => {
                // If there's a proper sequence component, use it
                if (sequenceComp) {
                  return currentPattern.components.map((comp) => {
                    if (comp.type === 'sequence') {
                      return String(seqValue).padStart(comp.length || 3, '0');
                    } else if (comp.type === 'static') {
                      return comp.value || '';
                    } else if (comp.type === 'year') {
                      return comp.format === 'YY' ? String(new Date().getFullYear()).slice(-2) : String(new Date().getFullYear());
                    }
                    return '';
                  }).join('');
                }
                
                // No sequence component - find the last numeric portion in STATIC components only
                // Build string from statics only to find the sequence
                let sequenceInfo: { compIndex: number; numStart: number; numLength: number } | null = null;
                
                currentPattern.components.forEach((comp, compIdx) => {
                  if (comp.type === 'static' && comp.value) {
                    // Find all numeric sequences in this static value
                    const numMatches = [...comp.value.matchAll(/\d{2,}/g)];
                    if (numMatches.length > 0) {
                      const lastNum = numMatches[numMatches.length - 1];
                      sequenceInfo = {
                        compIndex: compIdx,
                        numStart: lastNum.index!,
                        numLength: lastNum[0].length
                      };
                    }
                  }
                });
                
                // Now build the preview, replacing the identified sequence
                return currentPattern.components.map((comp, idx) => {
                  if (comp.type === 'static') {
                    if (sequenceInfo && idx === sequenceInfo.compIndex && comp.value) {
                      const val = comp.value;
                      const replacement = String(seqValue).padStart(sequenceInfo.numLength, '0');
                      return val.slice(0, sequenceInfo.numStart) + replacement + val.slice(sequenceInfo.numStart + sequenceInfo.numLength);
                    }
                    return comp.value || '';
                  } else if (comp.type === 'year') {
                    return comp.format === 'YY' ? String(new Date().getFullYear()).slice(-2) : String(new Date().getFullYear());
                  }
                  return '';
                }).join('');
              };
              
              return (
                <>
                  {/* Option: Start Fresh */}
                  <div 
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${sequenceStartOption === 'zero' ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700'}`}
                    onClick={() => setSequenceStartOption('zero')}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${sequenceStartOption === 'zero' ? 'border-primary' : 'border-slate-300'}`}>
                          {sequenceStartOption === 'zero' && <div className="w-2 h-2 rounded-full bg-primary" />}
                        </div>
                        <span className="font-medium text-sm">Start Fresh</span>
                      </div>
                      <span className="font-mono text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                        {getPreview(1)}
                      </span>
                    </div>
                  </div>

                  {/* Option: Continue */}
                  <div 
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${sequenceStartOption === 'continue' ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700'}`}
                    onClick={() => setSequenceStartOption('continue')}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${sequenceStartOption === 'continue' ? 'border-primary' : 'border-slate-300'}`}>
                          {sequenceStartOption === 'continue' && <div className="w-2 h-2 rounded-full bg-primary" />}
                        </div>
                        <span className="font-medium text-sm">Continue from</span>
                        <Input
                          type="number"
                          min={1}
                          value={sequenceStartNumber}
                          onChange={(e) => setSequenceStartNumber(parseInt(e.target.value) || 1)}
                          className="w-20 h-7 text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <span className="font-mono text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                        {getPreview(sequenceStartNumber)}
                      </span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          
          <DialogFooter className="pt-2">
            <Button variant="ghost" size="sm" onClick={() => setShowSequenceDialog(false)}>
              Cancel
            </Button>
            <Button 
              size="sm" 
              disabled={saving}
              onClick={async () => {
                if (!schoolID || !currentPattern.id) {
                  toast.error("Save the pattern first");
                  setShowSequenceDialog(false);
                  return;
                }
                
                const startValue = sequenceStartOption === 'zero' ? 1 : sequenceStartNumber;
                const updatedComponents = currentPattern.components.map(comp => {
                  if (comp.type === 'sequence') {
                    return { ...comp, min: startValue };
                  }
                  return comp;
                });
                
                setSaving(true);
                try {
                  // Deactivate other patterns first
                  const existingActive = patterns.find(p => p.isActive && p.id !== currentPattern.id);
                  if (existingActive && existingActive.id) {
                    await updateDoc(doc(db, "schools", schoolID, "admissionPatterns", existingActive.id), {
                      isActive: false
                    });
                  }
                  
                  // Save the current pattern as active
                  await updateDoc(doc(db, "schools", schoolID, "admissionPatterns", currentPattern.id), {
                    isActive: true,
                    components: updatedComponents,
                    updatedAt: new Date().toISOString()
                  });
                  
                  setCurrentPattern(prev => ({
                    ...prev,
                    isActive: true,
                    components: updatedComponents
                  }));
                  
                  toast.success(`Activated! Starting from ${String(startValue).padStart(3, '0')}`);
                  setShowSequenceDialog(false);
                } catch (error: any) {
                  console.error("Error activating:", error);
                  toast.error("Failed to activate");
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? (
                <>
                  <div className="h-4 w-4 mr-1 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Activate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <Trash2 className="h-5 w-5" />
              Delete Scheme
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this admission number scheme? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {patternToDelete && (
            <div className="py-4">
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm font-medium text-red-900 dark:text-red-100 mb-1">{patternToDelete.name}</p>
                <p className="text-lg font-mono text-red-700 dark:text-red-300">
                  {generatePreviewId(patternToDelete.components)}
                </p>
                {patternToDelete.isActive && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    This is the currently active scheme
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              disabled={isDeleting}
              className="hover:bg-red-700 dark:hover:bg-red-600 transition-colors"
              onClick={async () => {
                if (!patternToDelete || !schoolID) {
                  toast.error("Cannot delete: Missing pattern or school info");
                  return;
                }
                
                setIsDeleting(true);
                
                try {
                  // Delete from Firestore
                  if (patternToDelete.id) {
                    await deleteDoc(doc(db, "schools", schoolID, "admissionPatterns", patternToDelete.id));
                  }
                  
                  // Update local state
                  setPatterns(prev => prev.filter(p => p.id !== patternToDelete.id));
                  
                  // If this was the pattern being edited, reset to a new pattern
                  if (currentPattern.id === patternToDelete.id) {
                    setCurrentPattern({
                      name: "New Pattern",
                      template: "",
                      components: [
                        { id: crypto.randomUUID(), type: 'static', value: 'ADM', key: 'prefix' },
                        { id: crypto.randomUUID(), type: 'static', value: '/', key: 'sep1' },
                        { id: crypto.randomUUID(), type: 'year', format: 'YYYY', key: 'year' },
                        { id: crypto.randomUUID(), type: 'static', value: '/', key: 'sep2' },
                        { id: crypto.randomUUID(), type: 'sequence', length: 3, resetOn: 'year', min: 1, key: 'seq' }
                      ],
                      yearResetRule: 'calendar',
                      academicYearStart: { month: 9, day: 1 },
                      onOverflow: 'error',
                      isActive: false
                    });
                    setIsEditing(false);
                  }
                  
                  toast.success(`Deleted "${patternToDelete.name}"`);
                  setShowDeleteConfirm(false);
                  setPatternToDelete(null);
                } catch (error: any) {
                  console.error("Error deleting pattern:", error);
                  toast.error(`Failed to delete: ${error.message || 'Unknown error'}`);
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              {isDeleting ? (
                <>
                  <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Scheme
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster position="top-right" richColors />
    </SidebarProvider>
  );
};

export default AdmissionNumberSetup;
